import React, { useState } from "react";
import { toast } from "sonner";
import { ShieldAlert, Search, FileDown, AlertCircle } from "lucide-react";
import api from "../../lib/api";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";

const LABEL_STYLES = {
  NAME: { bg: "rgba(225,72,77,0.12)", color: "#B91C1C" },
  EMAIL: { bg: "rgba(20,184,184,0.14)", color: "#0E7B7B" },
  PHONE: { bg: "rgba(59,130,246,0.14)", color: "#1D4ED8" },
  ADDRESS: { bg: "rgba(245,158,11,0.14)", color: "#B45309" },
  DATE_OF_BIRTH: { bg: "rgba(139,92,246,0.14)", color: "#6D28D9" },
  SSN_MASKED: { bg: "rgba(225,72,77,0.18)", color: "#7F1D1D" },
  OTHER: { bg: "rgba(143,143,154,0.12)", color: "#52525B" },
};

function LabelPill({ label }) {
  const s = LABEL_STYLES[label] || LABEL_STYLES.OTHER;
  return (
    <span
      className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide"
      style={{ backgroundColor: s.bg, color: s.color }}
    >
      {label}
    </span>
  );
}

export default function RedactionPanel({ caseId, isReadOnly, onCaseRefreshed }) {
  const [data, setData] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [accepted, setAccepted] = useState({});

  const scan = async () => {
    setScanning(true);
    try {
      const { data: out } = await api.post(
        `/cases/${caseId}/copilot/suggest-redactions`,
      );
      setData(out);
      // Default: pre-accept every span
      const seed = {};
      (out.spans || []).forEach((s) => {
        if (s.scope === "case_details") seed[s.id] = true;
      });
      setAccepted(seed);
    } catch (e) {
      toast.error("Could not scan for PII");
    } finally {
      setScanning(false);
    }
  };

  const generatePdf = async () => {
    if (!data) return;
    const accepted_ids = Object.keys(accepted).filter((k) => accepted[k]);
    if (accepted_ids.length === 0) {
      toast.error("Select at least one span to redact");
      return;
    }
    setGenerating(true);
    try {
      const { data: out } = await api.post(
        `/cases/${caseId}/copilot/apply-redactions`,
        {
          accepted_span_ids: accepted_ids,
          spans: (data.spans || []).filter((s) => s.scope === "case_details"),
        },
      );
      toast.success(
        `Redacted PDF created (${out.span_count} spans applied). Listed in Documents tab.`,
      );
      onCaseRefreshed?.();
      // Reset
      setData(null);
      setAccepted({});
    } catch (e) {
      const detail = e?.response?.data?.detail || "Redacted PDF generation failed";
      toast.error(typeof detail === "string" ? detail : "Redaction failed");
    } finally {
      setGenerating(false);
    }
  };

  const caseDetailsSpans = (data?.spans || []).filter(
    (s) => s.scope === "case_details",
  );
  const filenameSpans = (data?.spans || []).filter((s) => s.scope === "filename");

  return (
    <div className="brand-card" data-testid="redaction-panel">
      <div className="flex items-start justify-between gap-3 border-b border-[color:var(--border-subtle)] px-5 py-4 sm:flex-row sm:items-center">
        <div className="flex items-start gap-3">
          <ShieldAlert className="mt-0.5 h-5 w-5 text-brand-gold" />
          <div>
            <h3 className="text-sm font-semibold text-[color:var(--page-fg)]">
              Smart redactions
            </h3>
            <p className="text-[11px] text-[color:var(--page-fg-muted)]">
              Identify PII and generate a redacted-copy PDF for external sharing.
              Original case file is never modified.
            </p>
          </div>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={scan}
          disabled={scanning || isReadOnly}
          data-testid="redaction-scan"
        >
          <Search className={`mr-1.5 h-3.5 w-3.5 ${scanning ? "animate-pulse" : ""}`} />
          {scanning ? "Scanning…" : "Suggest redactions"}
        </Button>
      </div>

      {data && (
        <div className="px-5 py-4">
          {data.spans?.length === 0 && (
            <p
              className="py-4 text-center text-sm text-[color:var(--page-fg-muted)]"
              data-testid="redaction-clean"
            >
              No PII detected in case details or document filenames.
            </p>
          )}

          {!data.llm_available && data.source === "regex_only" && (
            <div
              className="mb-3 flex items-start gap-2 rounded-lg border border-brand-gold/30 bg-brand-gold/10 px-3 py-2 text-xs text-[color:var(--page-fg)]"
              data-testid="redaction-llm-unavailable"
            >
              <AlertCircle className="mt-0.5 h-4 w-4 text-brand-gold" />
              <span>
                AI scan unavailable — showing regex-only matches (lower recall).
              </span>
            </div>
          )}

          {caseDetailsSpans.length > 0 && (
            <>
              <p className="mb-2 text-[11px] uppercase tracking-wider text-[color:var(--page-fg-muted)]">
                Case details · {caseDetailsSpans.length} span(s)
              </p>
              <div className="space-y-1 rounded-lg border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)]">
                {caseDetailsSpans.map((s) => (
                  <label
                    key={s.id}
                    className="flex cursor-pointer items-center gap-3 border-b border-[color:var(--border-subtle)] px-3 py-2 text-xs last:border-b-0"
                    data-testid={`redaction-span-${s.id}`}
                  >
                    <Checkbox
                      checked={!!accepted[s.id]}
                      onCheckedChange={(v) =>
                        setAccepted((a) => ({ ...a, [s.id]: !!v }))
                      }
                      data-testid={`redaction-checkbox-${s.id}`}
                    />
                    <LabelPill label={s.label} />
                    <span className="font-mono text-[11px] text-[color:var(--page-fg-secondary)]">
                      [{s.start}, {s.end}]
                    </span>
                    <span
                      className="ml-auto truncate font-mono text-[11px] italic text-[color:var(--page-fg-muted)]"
                      data-testid={`redaction-preview-${s.id}`}
                    >
                      preview: {s.redacted_preview}
                    </span>
                    <span className="rounded-full bg-white px-1.5 py-0.5 text-[10px] text-[color:var(--page-fg-secondary)]">
                      {s.source}
                    </span>
                  </label>
                ))}
              </div>
              <div className="mt-3 flex items-center justify-between">
                <p className="text-[11px] text-[color:var(--page-fg-muted)]">
                  {Object.values(accepted).filter(Boolean).length} of{" "}
                  {caseDetailsSpans.length} selected
                </p>
                <Button
                  size="sm"
                  onClick={generatePdf}
                  disabled={
                    isReadOnly ||
                    generating ||
                    Object.values(accepted).filter(Boolean).length === 0
                  }
                  data-testid="redaction-generate-pdf"
                  className="bg-brand-teal text-white hover:bg-brand-tealDeep"
                >
                  <FileDown className="mr-1.5 h-3.5 w-3.5" />
                  {generating ? "Generating PDF…" : "Generate redacted PDF"}
                </Button>
              </div>
            </>
          )}

          {filenameSpans.length > 0 && (
            <div className="mt-4">
              <p className="mb-2 text-[11px] uppercase tracking-wider text-[color:var(--page-fg-muted)]">
                Document filenames flagged · {filenameSpans.length}
              </p>
              <div className="space-y-1 rounded-lg border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] px-3 py-2 text-xs">
                {filenameSpans.map((s) => (
                  <div
                    key={s.id}
                    className="flex items-center gap-3 border-b border-[color:var(--border-subtle)] py-1 last:border-b-0"
                    data-testid={`redaction-filename-${s.id}`}
                  >
                    <LabelPill label={s.label} />
                    <span className="font-mono text-[11px]">
                      preview: {s.redacted_preview}
                    </span>
                    <span className="ml-auto text-[10px] text-[color:var(--page-fg-muted)]">
                      Doc {(s.doc_id || "").slice(0, 8)}…
                    </span>
                  </div>
                ))}
              </div>
              <p className="mt-1 text-[10px] italic text-[color:var(--page-fg-muted)]">
                Filenames are not auto-redacted. Re-upload renamed copies if
                needed.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
